import { useState, useEffect, useMemo, useCallback } from "react";
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Clock, User, Scissors, Calendar as CalendarIcon, Phone, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getCitas, getPeluqueros, getServicios, createCitaAdmin, deleteCita, getBloqueos, createBloqueo, deleteBloqueo, getConfiguracion } from "@/lib/api";

const SLOT_MINUTES = 20;
const SLOT_HEIGHT = 48;
const GRID_START_HOUR = 9;
const GRID_END_HOUR = 21;
const TOTAL_SLOTS = ((GRID_END_HOUR - GRID_START_HOUR) * 60) / SLOT_MINUTES;

function minutesSinceGridStart(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  return (h - GRID_START_HOUR) * 60 + m;
}

function minutesToTime(m) {
  const totalMin = GRID_START_HOUR * 60 + m;
  return `${Math.floor(totalMin / 60).toString().padStart(2, "0")}:${(totalMin % 60).toString().padStart(2, "0")}`;
}

function timeToMin(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function generateGridTimes() {
  const times = [];
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    times.push(minutesToTime(i * SLOT_MINUTES));
  }
  return times;
}

const GRID_TIMES = generateGridTimes();

function getWorkingRanges(peluquero, dayOfWeek) {
  const horario = peluquero.horarios?.find(h => h.dia === dayOfWeek && h.activo);
  if (!horario) return [];
  const ranges = [{ inicio: timeToMin(horario.inicio), fin: timeToMin(horario.fin) }];
  if (horario.inicio2 && horario.fin2) {
    ranges.push({ inicio: timeToMin(horario.inicio2), fin: timeToMin(horario.fin2) });
  }
  return ranges;
}

function isInWorkingHours(slotMinutes, workingRanges) {
  const absMinutes = GRID_START_HOUR * 60 + slotMinutes;
  return workingRanges.some(r => absMinutes >= r.inicio && absMinutes + SLOT_MINUTES <= r.fin);
}

export default function AdminDashboard() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [peluqueros, setPeluqueros] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [citas, setCitas] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCitaModal, setShowCitaModal] = useState(false);
  const [showBloqueoModal, setShowBloqueoModal] = useState(false);
  const [showCitaDetailModal, setShowCitaDetailModal] = useState(false);
  const [selectedCita, setSelectedCita] = useState(null);
  const [vistaPeluquero, setVistaPeluquero] = useState(null); // null = Todos, else peluquero.id

  const [newCita, setNewCita] = useState({
    cliente_nombre: "", cliente_telefono: "", servicio_id: "", peluquero_id: "",
    fecha: format(new Date(), "yyyy-MM-dd"), hora_inicio: "10:00"
  });
  const [newBloqueo, setNewBloqueo] = useState({
    peluquero_id: "", fecha: format(new Date(), "yyyy-MM-dd"),
    hora_inicio: "12:00", hora_fin: "13:00", motivo: "Descanso"
  });

  useEffect(() => { loadInitialData(); }, []);

  const loadCitas = useCallback(async () => {
    setLoading(true);
    try {
      const fechaInicio = format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const fechaFin = format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const [citasRes, bloqueosRes] = await Promise.all([
        getCitas({ fecha_inicio: fechaInicio, fecha_fin: fechaFin }),
        getBloqueos({ fecha_inicio: fechaInicio, fecha_fin: fechaFin })
      ]);
      setCitas(citasRes.data);
      setBloqueos(bloqueosRes.data);
    } catch { toast.error("Error al cargar citas"); }
    finally { setLoading(false); }
  }, [currentDate]);

  useEffect(() => { loadCitas(); }, [loadCitas]);

  const loadInitialData = async () => {
    try {
      const [peluquerosRes, serviciosRes, configRes] = await Promise.all([
        getPeluqueros(), getServicios(), getConfiguracion()
      ]);
      setPeluqueros(peluquerosRes.data.filter(p => p.activo));
      setServicios(serviciosRes.data);
      setConfig(configRes.data);
    } catch { toast.error("Error al cargar datos"); }
  };

  const festivos = useMemo(() => {
    return (config?.festivos || []).map(f => f.fecha);
  }, [config]);

  const weekDates = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const isDayBlocked = (date, peluquero) => {
    const fechaStr = format(date, "yyyy-MM-dd");
    if (festivos.includes(fechaStr)) return "Festivo";
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
    const horario = peluquero.horarios?.find(h => h.dia === dayOfWeek && h.activo);
    if (!horario) return "No trabaja";
    const diasLibres = peluquero.dias_libres || [];
    if (diasLibres.some(dl => dl.fecha === fechaStr)) return "Dia libre";
    return null;
  };

  const getEventStyle = (horaInicio, horaFin) => {
    const top = (minutesSinceGridStart(horaInicio) / SLOT_MINUTES) * SLOT_HEIGHT;
    const duration = minutesSinceGridStart(horaFin) - minutesSinceGridStart(horaInicio);
    const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
    return { top, height: Math.max(height, 24) };
  };

  const getNonWorkingBlocks = (date, peluquero) => {
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
    const workRanges = getWorkingRanges(peluquero, dayOfWeek);
    if (workRanges.length === 0) return [];

    const gridStartMin = GRID_START_HOUR * 60;
    const gridEndMin = GRID_END_HOUR * 60;
    const blocks = [];

    let cursor = gridStartMin;
    for (const range of workRanges.sort((a, b) => a.inicio - b.inicio)) {
      if (cursor < range.inicio) {
        blocks.push({ start: cursor - gridStartMin, end: range.inicio - gridStartMin, label: "Descanso" });
      }
      cursor = range.fin;
    }
    if (cursor < gridEndMin) {
      blocks.push({ start: cursor - gridStartMin, end: gridEndMin - gridStartMin, label: "Descanso" });
    }
    return blocks;
  };

  const getCitasFor = (fecha, peluqueroId) => {
    const fechaStr = format(fecha, "yyyy-MM-dd");
    return citas.filter(c => c.fecha === fechaStr && c.peluquero_id === peluqueroId && c.estado !== "cancelada");
  };

  const getBloqueosFor = (fecha, peluqueroId) => {
    const fechaStr = format(fecha, "yyyy-MM-dd");
    return bloqueos.filter(b => b.fecha === fechaStr && b.peluquero_id === peluqueroId);
  };

  const getValidHoursForModal = () => {
    const pel = peluqueros.find(p => p.id === newCita.peluquero_id);
    if (!pel || !newCita.fecha) return GRID_TIMES;
    const fecha = new Date(newCita.fecha + "T00:00:00");
    const dayOfWeek = fecha.getDay() === 0 ? 6 : fecha.getDay() - 1;
    const ranges = getWorkingRanges(pel, dayOfWeek);
    if (ranges.length === 0) return [];
    const serv = servicios.find(s => s.id === newCita.servicio_id);
    const dur = serv?.duracion_minutos || SLOT_MINUTES;
    const hours = [];
    for (const range of ranges) {
      let t = range.inicio;
      while (t + dur <= range.fin) {
        hours.push(`${Math.floor(t / 60).toString().padStart(2, "0")}:${(t % 60).toString().padStart(2, "0")}`);
        t += dur;
      }
    }
    return hours;
  };

  const handleOpenCitaModal = (peluqueroId = null, fecha = null, hora = null) => {
    setNewCita({
      cliente_nombre: "", cliente_telefono: "",
      servicio_id: servicios.filter(s => s.activo)[0]?.id || "",
      peluquero_id: peluqueroId || peluqueros[0]?.id || "",
      fecha: fecha || format(new Date(), "yyyy-MM-dd"),
      hora_inicio: hora || "10:00"
    });
    setShowCitaModal(true);
  };

  const handleOpenBloqueoModal = (peluqueroId = null, fecha = null, hora = null) => {
    setNewBloqueo({
      peluquero_id: peluqueroId || peluqueros[0]?.id || "",
      fecha: fecha || format(new Date(), "yyyy-MM-dd"),
      hora_inicio: hora || "12:00",
      hora_fin: hora ? `${parseInt(hora.split(":")[0]) + 1}:00` : "13:00",
      motivo: "Descanso"
    });
    setShowBloqueoModal(true);
  };

  const handleCreateCita = async () => {
    if (!newCita.cliente_nombre || !newCita.cliente_telefono || !newCita.servicio_id || !newCita.peluquero_id) {
      toast.error("Completa todos los campos"); return;
    }
    try {
      await createCitaAdmin(newCita);
      toast.success("Cita creada");
      setShowCitaModal(false);
      loadCitas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cita");
    }
  };

  const handleCreateBloqueo = async () => {
    if (!newBloqueo.peluquero_id) { toast.error("Selecciona un peluquero"); return; }
    try {
      await createBloqueo(newBloqueo);
      toast.success("Bloqueo creado");
      setShowBloqueoModal(false);
      loadCitas();
    } catch { toast.error("Error al crear bloqueo"); }
  };

  const handleDeleteCita = async () => {
    if (!selectedCita) return;
    try {
      await deleteCita(selectedCita.id);
      toast.success("Cita eliminada");
      setShowCitaDetailModal(false);
      setSelectedCita(null);
      loadCitas();
    } catch { toast.error("Error al eliminar cita"); }
  };

  const handleDeleteBloqueo = async (bloqueoId) => {
    try {
      await deleteBloqueo(bloqueoId);
      toast.success("Bloqueo eliminado");
      loadCitas();
    } catch { toast.error("Error al eliminar bloqueo"); }
  };

  const handlePrev = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNext = () => setCurrentDate(addWeeks(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const getTitle = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return `${format(start, "d MMM", { locale: es })} - ${format(end, "d MMM yyyy", { locale: es })}`;
  };

  const validModalHours = useMemo(getValidHoursForModal, [newCita.peluquero_id, newCita.fecha, newCita.servicio_id, peluqueros, servicios]);

  const peluquerosVista = vistaPeluquero ? peluqueros.filter(p => p.id === vistaPeluquero) : peluqueros;

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Calendario</h1>
          <p className="text-muted-foreground">Vista semanal - Slots de {SLOT_MINUTES} min</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="h-10 gap-2" onClick={() => handleOpenBloqueoModal()} data-testid="btn-add-bloqueo">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Bloquear hora</span>
          </Button>
          <Button className="h-10 gap-2" onClick={() => handleOpenCitaModal()} data-testid="btn-add-cita">
            <Plus className="h-4 w-4" />
            Nueva cita
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrev} data-testid="btn-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" onClick={handleToday} data-testid="btn-today">Hoy</Button>
              <Button variant="outline" size="icon" onClick={handleNext} data-testid="btn-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <span className="font-medium text-lg capitalize">{getTitle()}</span>
            <div className="flex items-center gap-2">
              {peluqueros.map(p => (
                <div key={p.id} className="flex items-center gap-1 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                  <span className="hidden md:inline">{p.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selector de peluquero + leyenda de colores */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="vista-selector">
            <span className="text-sm text-muted-foreground mr-1">Ver:</span>
            <Button
              variant={vistaPeluquero === null ? "default" : "outline"}
              size="sm"
              onClick={() => setVistaPeluquero(null)}
              data-testid="vista-todos"
            >
              Todos
            </Button>
            {peluqueros.map(p => (
              <Button
                key={p.id}
                variant={vistaPeluquero === p.id ? "default" : "outline"}
                size="sm"
                onClick={() => setVistaPeluquero(p.id)}
                data-testid={`vista-${p.id}`}
              >
                <span className="w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
                {p.nombre}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3" data-testid="calendar-legend">
            {peluquerosVista.map(p => (
              <div key={p.id} className="flex items-center gap-1.5 text-sm">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                <span>{p.nombre}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner w-10 h-10" /></div>
      ) : peluqueros.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg">No hay peluqueros activos</p>
        </CardContent></Card>
      ) : (
        <Card className="overflow-hidden">
          <ScrollArea className="w-full" orientation="horizontal">
            <div className="min-w-[900px]">
              {/* Header */}
              <div className="flex border-b bg-muted/50 sticky top-0 z-20">
                <div className="w-14 flex-shrink-0 border-r" />
                {weekDates.map((date, dayIdx) => {
                  const isFestivo = festivos.includes(format(date, "yyyy-MM-dd"));
                  return (
                    <div key={dayIdx} className={`flex-1 border-r last:border-r-0 ${isFestivo ? "bg-red-50" : isToday(date) ? "bg-accent/10" : ""}`}>
                      <div className="text-center py-2 border-b">
                        <div className="text-xs text-muted-foreground uppercase">{format(date, "EEE", { locale: es })}</div>
                        <div className={`text-lg font-medium ${isFestivo ? "text-red-500 line-through" : isToday(date) ? "text-accent" : ""}`}>
                          {format(date, "d")}
                        </div>
                        {isFestivo && <span className="text-[10px] text-red-500 font-medium">FESTIVO</span>}
                      </div>
                      <div className="flex">
                        {peluquerosVista.map((p, pIdx) => (
                          <div key={p.id} className={`flex-1 text-center py-1 text-xs truncate px-1 ${pIdx < peluquerosVista.length - 1 ? "border-r" : ""}`}>
                            <span className="hidden lg:inline">{p.nombre}</span>
                            <span className="lg:hidden">{p.nombre.charAt(0)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time Grid */}
              <div className="relative">
                {GRID_TIMES.map((hora, slotIdx) => (
                  <div key={hora} className="flex border-b" style={{ height: SLOT_HEIGHT }}>
                    <div className="w-14 flex-shrink-0 border-r text-[11px] text-muted-foreground text-right pr-1 pt-0.5">
                      {hora.endsWith(":00") || hora.endsWith(":20") || hora.endsWith(":40") ? hora : ""}
                    </div>
                    {weekDates.map((date, dayIdx) => {
                      const isFestivo = festivos.includes(format(date, "yyyy-MM-dd"));
                      return (
                        <div key={dayIdx} className={`flex-1 border-r last:border-r-0 flex ${isToday(date) && !isFestivo ? "bg-accent/5" : ""}`}>
                          {peluquerosVista.map((p, pIdx) => {
                            const blocked = isDayBlocked(date, p);
                            const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
                            const workRanges = getWorkingRanges(p, dayOfWeek);
                            const slotMin = slotIdx * SLOT_MINUTES;
                            const isWorking = !blocked && isInWorkingHours(slotMin, workRanges);
                            const fechaStr = format(date, "yyyy-MM-dd");

                            return (
                              <div
                                key={p.id}
                                className={`flex-1 relative ${pIdx < peluquerosVista.length - 1 ? "border-r border-dashed" : ""} ${
                                  blocked || !isWorking
                                    ? "bg-gray-100 cursor-default"
                                    : "cursor-pointer hover:bg-accent/5"
                                }`}
                                style={{ borderColor: "#e5e5e5" }}
                                onClick={() => {
                                  if (!blocked && isWorking) {
                                    handleOpenCitaModal(p.id, fechaStr, hora);
                                  }
                                }}
                              >
                                {/* Non-working slot indicator */}
                                {(blocked || !isWorking) && slotIdx === 0 && (
                                  <div className="absolute inset-0 z-[5] flex items-start justify-center pointer-events-none"
                                    style={{ top: 0, height: SLOT_HEIGHT * TOTAL_SLOTS }}>
                                  </div>
                                )}

                                {/* Render citas only from first slot */}
                                {slotIdx === 0 && getCitasFor(date, p.id).map(cita => {
                                  const style = getEventStyle(cita.hora_inicio, cita.hora_fin);
                                  return (
                                    <div key={cita.id}
                                      className="absolute left-0.5 right-0.5 rounded-sm px-1 py-0.5 cursor-pointer overflow-hidden text-white text-[11px] z-10 hover:opacity-90 transition-opacity"
                                      style={{ top: style.top, height: style.height, backgroundColor: p.color }}
                                      onClick={(e) => { e.stopPropagation(); setSelectedCita(cita); setShowCitaDetailModal(true); }}
                                    >
                                      <div className="font-medium truncate">{cita.hora_inicio} · {(cita.peluquero_nombre || "").split(" ")[0]}</div>
                                      <div className="truncate">{cita.cliente_nombre}</div>
                                      {style.height > 40 && <div className="truncate opacity-80">{cita.servicio_nombre}</div>}
                                    </div>
                                  );
                                })}

                                {/* Render bloqueos from first slot */}
                                {slotIdx === 0 && getBloqueosFor(date, p.id).map(bloqueo => {
                                  const style = getEventStyle(bloqueo.hora_inicio, bloqueo.hora_fin);
                                  return (
                                    <div key={bloqueo.id}
                                      className="absolute left-0.5 right-0.5 rounded-sm px-1 py-0.5 bg-gray-400 text-white text-[11px] z-10 cursor-pointer group"
                                      style={{ top: style.top, height: style.height }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex justify-between items-start">
                                        <span className="truncate">{bloqueo.motivo}</span>
                                        <button className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDeleteBloqueo(bloqueo.id)}>
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Non-working overlay blocks */}
                                {slotIdx === 0 && !blocked && getNonWorkingBlocks(date, p).map((block, bi) => {
                                  const top = (block.start / SLOT_MINUTES) * SLOT_HEIGHT;
                                  const height = ((block.end - block.start) / SLOT_MINUTES) * SLOT_HEIGHT;
                                  return (
                                    <div key={`nw-${bi}`}
                                      className="absolute left-0 right-0 z-[4] flex items-center justify-center pointer-events-none"
                                      style={{ top, height, background: "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 8px)" }}
                                    >
                                      {height > 30 && <span className="text-[10px] text-gray-400 font-medium bg-white/70 px-1 rounded">Descanso</span>}
                                    </div>
                                  );
                                })}

                                {/* Full day blocked overlay */}
                                {slotIdx === 0 && blocked && (
                                  <div
                                    className="absolute left-0 right-0 z-[4] flex items-center justify-center pointer-events-none"
                                    style={{
                                      top: 0,
                                      height: SLOT_HEIGHT * TOTAL_SLOTS,
                                      background: blocked === "Festivo"
                                        ? "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(239,68,68,0.08) 4px, rgba(239,68,68,0.08) 8px)"
                                        : "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 8px)"
                                    }}
                                  >
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${blocked === "Festivo" ? "bg-red-100 text-red-500" : "bg-gray-200 text-gray-500"}`}>
                                      {blocked}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-heading text-accent">{citas.filter(c => c.estado !== "cancelada").length}</div>
          <div className="text-sm text-muted-foreground">Citas esta semana</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-heading">{citas.filter(c => c.estado === "cancelada").length}</div>
          <div className="text-sm text-muted-foreground">Canceladas</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-heading text-accent">{peluqueros.length}</div>
          <div className="text-sm text-muted-foreground">Peluqueros</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-heading">{bloqueos.length}</div>
          <div className="text-sm text-muted-foreground">Bloqueos</div>
        </CardContent></Card>
      </div>

      {/* New Cita Modal */}
      <Dialog open={showCitaModal} onOpenChange={setShowCitaModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva cita manual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre del cliente *</Label>
              <Input placeholder="Nombre del cliente" value={newCita.cliente_nombre}
                onChange={e => setNewCita({ ...newCita, cliente_nombre: e.target.value })} className="h-12" data-testid="modal-input-nombre" />
            </div>
            <div>
              <Label>Telefono *</Label>
              <Input placeholder="Telefono" value={newCita.cliente_telefono}
                onChange={e => setNewCita({ ...newCita, cliente_telefono: e.target.value })} className="h-12" data-testid="modal-input-telefono" />
            </div>
            <div>
              <Label>Servicio *</Label>
              <Select value={newCita.servicio_id} onValueChange={v => setNewCita({ ...newCita, servicio_id: v })}>
                <SelectTrigger className="h-12"><SelectValue placeholder="Seleccionar servicio" /></SelectTrigger>
                <SelectContent>
                  {servicios.filter(s => s.activo).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre} ({s.duracion_minutos} min - {s.precio}€)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Peluquero *</Label>
              <Select value={newCita.peluquero_id} onValueChange={v => setNewCita({ ...newCita, peluquero_id: v })}>
                <SelectTrigger className="h-12"><SelectValue placeholder="Seleccionar peluquero" /></SelectTrigger>
                <SelectContent>
                  {peluqueros.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha *</Label>
                <Input type="date" value={newCita.fecha} onChange={e => setNewCita({ ...newCita, fecha: e.target.value })} className="h-12" />
                {festivos.includes(newCita.fecha) && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><Ban className="h-3 w-3" /> Dia festivo</p>
                )}
              </div>
              <div>
                <Label>Hora *</Label>
                <Select value={newCita.hora_inicio} onValueChange={v => setNewCita({ ...newCita, hora_inicio: v })}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {validModalHours.length > 0 ? validModalHours.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    )) : (
                      <div className="p-3 text-sm text-muted-foreground text-center">No hay horas disponibles</div>
                    )}
                  </SelectContent>
                </Select>
                {validModalHours.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">El peluquero no trabaja este dia</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCitaModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateCita} data-testid="modal-btn-crear-cita"
              disabled={validModalHours.length === 0 || festivos.includes(newCita.fecha)}>Crear cita</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bloqueo Modal */}
      <Dialog open={showBloqueoModal} onOpenChange={setShowBloqueoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Bloquear horario</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Peluquero *</Label>
              <Select value={newBloqueo.peluquero_id} onValueChange={v => setNewBloqueo({ ...newBloqueo, peluquero_id: v })}>
                <SelectTrigger className="h-12"><SelectValue placeholder="Seleccionar peluquero" /></SelectTrigger>
                <SelectContent>
                  {peluqueros.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={newBloqueo.fecha} onChange={e => setNewBloqueo({ ...newBloqueo, fecha: e.target.value })} className="h-12" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hora inicio</Label>
                <Select value={newBloqueo.hora_inicio} onValueChange={v => setNewBloqueo({ ...newBloqueo, hora_inicio: v })}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRID_TIMES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hora fin</Label>
                <Select value={newBloqueo.hora_fin} onValueChange={v => setNewBloqueo({ ...newBloqueo, hora_fin: v })}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRID_TIMES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Motivo</Label>
              <Input placeholder="Ej: Descanso, Comida, Vacaciones..." value={newBloqueo.motivo}
                onChange={e => setNewBloqueo({ ...newBloqueo, motivo: e.target.value })} className="h-12" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBloqueoModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateBloqueo}>Crear bloqueo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cita Detail Modal */}
      <Dialog open={showCitaDetailModal} onOpenChange={setShowCitaDetailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Detalles de la cita</DialogTitle></DialogHeader>
          {selectedCita && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{selectedCita.cliente_nombre}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{selectedCita.cliente_telefono}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Scissors className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{selectedCita.servicio_nombre}</p>
                  <p className="text-sm text-muted-foreground">{selectedCita.duracion_minutos} min</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                <p>{format(parseISO(selectedCita.fecha), "EEEE d 'de' MMMM", { locale: es })}</p>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <p>{selectedCita.hora_inicio} - {selectedCita.hora_fin}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: peluqueros.find(p => p.id === selectedCita.peluquero_id)?.color || "#3A5A40" }} />
                <p>{selectedCita.peluquero_nombre}</p>
              </div>
              <div className="pt-4 border-t flex items-center justify-between">
                <p className="text-2xl font-heading text-accent">{selectedCita.precio}€</p>
                <Badge variant={selectedCita.estado === "cancelada" ? "destructive" : "default"}>{selectedCita.estado}</Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCitaDetailModal(false)}>Cerrar</Button>
            {selectedCita?.estado !== "cancelada" && (
              <Button variant="destructive" onClick={handleDeleteCita}>Eliminar cita</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
